/**
 * src/pages/WhatIsConsciousness/sections/HardProblemSection.tsx
 * Clear, engaging breakdown of Chalmers' "easy" vs "hard" problems of consciousness,
 * the explanatory gap, and philosophical perspectives.
 */

import React from "react";
import { CheckCircle2, HelpCircle, ArrowRight } from "lucide-react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import "@/components/cosmic/cosmic.css";
export function HardProblemSection() {
  return <section className="w-full max-w-full min-w-0" aria-labelledby="hard-problem-heading">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span className="ql-eyebrow">Philosophical Foundations</span>
          </div>
          <h2 id="hard-problem-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            The Hard Problem & The Explanatory Gap
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <EvidenceBadge level="philosophy" />
          <Footnote n={1} />
          <Footnote n={2} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0">
        {/* The Easy Problems */}
        <div className="ql-glass p-6 sm:p-7 flex flex-col justify-between min-w-0 border-primary/20">
          <div>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/30">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                <span>Cognitive Mechanics</span>
              </span>
              <span className="font-mono text-xs text-muted-foreground bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
                Solvable via Science
              </span>
            </div>

            <h3 className="font-serif text-xl font-bold text-foreground mb-3">
              The "Easy" Problems
            </h3>

            <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-4 break-words">
              Formulated by philosopher David Chalmers in 1995. Calling them "easy" is not
              to say they are simple—they represent centuries of neuroscience—but they are
              methodologically tractable via standard cognitive science.
            </p>

            <ul className="space-y-2.5 font-sans text-xs sm:text-sm text-foreground/90 pl-0 list-none mb-6">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1 font-mono text-xs">01</span>
                <span>How the brain discriminates optical wavelengths and acoustic frequencies.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1 font-mono text-xs">02</span>
                <span>The integration of multi-sensory information into cohesive working memory.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1 font-mono text-xs">03</span>
                <span>How mental states steer deliberate motor behavior and verbal communication.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1 font-mono text-xs">04</span>
                <span>The neurochemistry governing transitions between waking, dreaming, and coma.</span>
              </li>
            </ul>
          </div>

          <div className="p-3.5 rounded-lg bg-card/60 border border-border/40 text-xs font-mono text-muted-foreground">
            Target: Computational architecture, neural firing dynamics, feedback circuitry.
          </div>
        </div>

        {/* The Hard Problem */}
        <div className="ql-glass p-6 sm:p-7 flex flex-col justify-between min-w-0 border-border shadow-[0_0_24px_-10px_var(--glow-laser)]">
          <div>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/30">
              <span className="font-mono text-xs uppercase tracking-wider text-foreground font-semibold flex items-center gap-1.5">
                <HelpCircle className="size-4 text-primary" />
                <span>Subjective Experience</span>
              </span>
              <span className="font-mono text-xs text-foreground bg-accent px-2 py-0.5 rounded border border-border">
                The Explanatory Gap
              </span>
            </div>

            <h3 className="font-serif text-xl font-bold text-foreground mb-3">
              The "Hard" Problem
            </h3>

            <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-4 break-words">
              Why should any physical computational process feel like anything at all?
              Even after charting every neuron, ion pump, and neurotransmitter vesicle, an
              explanatory chasm remains.
            </p>

            <div className="p-4 rounded-lg bg-card border border-border mb-4 text-xs sm:text-sm text-foreground leading-relaxed font-sans italic">
              "Why should physical processing give rise to a rich inner life at all?
              It seems objectively reasonable that mental states could occur in total darkness,
              with no accompanying inner sensation."
              <span className="block mt-1 font-mono text-xs not-italic text-muted-foreground">
                — David Chalmers (1995)
              </span>
            </div>

            <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6 break-words">
              Qualia—the felt redness of a sunset, the agony of a toothache, the taste of wine—cannot
              be completely deduced solely by inspecting extracellular voltages.
            </p>
          </div>

          <div className="p-3.5 rounded-lg bg-card/60 border border-border text-xs font-mono text-muted-foreground">
            Target: The qualitative "what it is like" to be an experiencing subject (Nagel 1974).
          </div>
        </div>
      </div>

      {/* Thought Experiment Summary Box */}
      <div className="mt-6 ql-card p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-primary font-semibold uppercase tracking-wider">
              Classic Thought Experiment: The Philosophical Zombie
            </span>
          </div>
          <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
            Imagine a being physically, behaviorally, and chemically identical to you in every atom,
            reacting identically to stimuli, smiling and laughing—yet with zero inner conscious light.
            If such a "zombie" is even conceptually coherent, physical facts alone may not logically
            exhaust the nature of consciousness.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1.5 font-mono text-xs text-primary">
          <span>Conceptual Model</span>
          <ArrowRight className="size-3.5" />
        </div>
      </div>
    </section>;
}